import { formatDuration } from '../lib/format';
import { isSummary, type BrowseMovie } from '../lib/movie';
import { useI18n } from '../lib/use-i18n';
import type { AvailabilityStatus, WatchProgress } from '../lib/types';

/** The viewer only needs to know whether a title is waiting, being prepared or unavailable. */
function waitingKey(status: AvailabilityStatus) {
  if (status === 'failed') return 'temporarilyUnavailable' as const;
  if (status === 'queued' || status === 'searching' || status === 'pending') return 'inQueue' as const;
  return 'preparing' as const;
}

function artStyle(movie: BrowseMovie): React.CSSProperties {
  const image = movie.poster;
  return image ? { backgroundImage: `url("${image}")` } : {};
}

export function MovieCard({ movie, progress, index, onOpen, compact = false }: {
  movie: BrowseMovie;
  progress?: WatchProgress;
  index: number;
  onOpen: (movie: BrowseMovie) => void;
  compact?: boolean;
}) {
  const { locale, t } = useI18n();
  const available = isSummary(movie) ? movie.availability.playbackReady : movie.inLibrary;
  const percent = progress?.duration ? Math.min(100, progress.currentTime / progress.duration * 100) : 0;
  return (
    <button className={`catalog-card ${compact ? 'compact' : ''}`} data-focusable="true" onClick={() => onOpen(movie)}>
      <span className="catalog-art" style={artStyle(movie)}>
        <i>{String(index + 1).padStart(2, '0')}</i>
        {!movie.poster && <span className="no-art">{t('noArt')}</span>}
        {!available && <span className="status-chip">{isSummary(movie) ? `${movie.availability.progress}%` : movie.type === 'series' ? t('series') : t('available')}</span>}
        <span className="card-play">{available ? '▶' : '→'}</span>
      </span>
      <span className="catalog-copy">
        <b>{movie.title}</b>
        <small>{isSummary(movie) ? `${movie.year ?? '—'} · ${formatDuration(movie.durationSec ?? (movie.runtime ? movie.runtime * 60 : null), locale)}` : movie.year ?? '—'}</small>
        {isSummary(movie) && !movie.availability.playbackReady && <em>{t(waitingKey(movie.availability.status))}</em>}
      </span>
      {percent > 0 && <span className="watch-progress"><i style={{ width: `${percent}%` }} /></span>}
    </button>
  );
}

export function MovieRail({ title, eyebrow, movies, progress, onOpen, action }: {
  title: string;
  eyebrow: string;
  movies: BrowseMovie[];
  progress?: WatchProgress[];
  onOpen: (movie: BrowseMovie) => void;
  action?: React.ReactNode;
}) {
  if (!movies.length) return null;
  return (
    <section className="catalog-rail">
      <div className="section-heading">
        <div><span>{eyebrow}</span><h2>{title}</h2></div>{action}
      </div>
      <div className="catalog-row">
        {movies.map((movie, index) => (
          <MovieCard
            key={'id' in movie ? movie.id : movie.tmdbId}
            movie={movie}
            index={index}
            progress={'id' in movie ? progress?.find((item) => item.movieId === movie.id) : undefined}
            onOpen={onOpen}
          />
        ))}
      </div>
    </section>
  );
}
